import { useCallback } from 'react';

import { useAppStore, getAppStore } from '@/store';
import pollUntilCompleted from '@/requests/magic-flow/pollUntilCompleted';
import { clearGeneration } from '@/utils/storage/generationStorage';
import { MAGIC_FLOW_WEBHOOK_URL } from '@/consts/urls';
import buildFlowMediaList from '@/utils/generation/buildFlowMediaList';
import applyRunResults from '@/utils/generation/applyRunResults';
import decidePostGenerationUX from '@/utils/generation/decidePostGenerationUX';

import type { IAppStore } from '@/store';
import type { FlowMedia } from '@/types';

export interface PollingResult {
  resultFile: FlowMedia;
  isVideo: boolean;
  isAudio: boolean;
  driveUid?: string;
  width?: number;
  height?: number;
}

const selector = (store: IAppStore) => ({
  setResultFile: store.setResultFile,
  setGenerationTimestamp: store.setGenerationTimestamp,
  incrementPendingReadyCount: store.incrementPendingReadyCount,
});

/**
 * Hook for handling generation polling and result processing
 */
const useGenerationPolling = () => {
  const { setResultFile, setGenerationTimestamp, incrementPendingReadyCount } =
    useAppStore(selector);

  const poll = useCallback(
    async ({
      generationId,
      placeholderUuid,
      presetTitle,
      runId = null,
    }: {
      generationId: string;
      placeholderUuid: string;
      presetTitle: string;
      /** Stamped on every file so the grid can regroup the run. */
      runId?: string | null;
    }): Promise<PollingResult> => {
      const pollUrl = `${MAGIC_FLOW_WEBHOOK_URL}/${generationId}/result`;
      const pollingResponse = await pollUntilCompleted({ url: pollUrl });

      // A flow with `outputsCount > 1` answers with several results from the one
      // job, so the whole list is landed — otherwise a multi-output run shows as
      // one tile and N-1 files reachable only from Drive.
      const built = buildFlowMediaList({ generationId, pollingResponse });
      const [primary] = built;
      const { resultFile, isVideo, isAudio } = primary;

      setResultFile(resultFile);
      const timestamp = Date.now();
      setGenerationTimestamp(timestamp);

      const { screen: currentScreen } = getAppStore();

      await applyRunResults({
        built,
        placeholderUuid,
        presetTitle,
        runId,
        timestamp,
      });

      await clearGeneration(generationId);

      const decision = decidePostGenerationUX({ screen: currentScreen });

      if (decision.kind === 'notify') {
        // Surface completion via the "creations ready" toast (a tap takes the
        // user to the Creations grid). Never auto-open/navigate.
        // Counted per file, so a multi-output run reads "2 creations ready"
        // rather than announcing one and hiding the rest.
        incrementPendingReadyCount(built.length);
      }

      return {
        resultFile,
        isVideo,
        isAudio,
        driveUid: resultFile.driveUid,
        width: resultFile.width,
        height: resultFile.height,
      };
    },
    [setResultFile, setGenerationTimestamp, incrementPendingReadyCount]
  );

  return { poll };
};

export default useGenerationPolling;
